/**
 * Versteckte Events-Seite (/events): gemeinsame Typen, Prüfregeln und
 * Konstanten für öffentliche Seite, Backend-Pflege und Serverfunktionen.
 */
import { z } from "zod";

export const EVENTS_PATH = "/events";
export const EVENTS_CANCEL_PATH = "/events/absagen";

export const EVENT_REMINDER_DAYS = [7, 1] as const;

export type PublicEvent = {
  id: string;
  title: string;
  startsAt: string;
  location: string;
  contentMd: string;
  registrationOpen: boolean;
  closedMessage: string;
  /** Freie Plätze oder null bei unbegrenzter Kapazität. */
  freeSeats: number | null;
};

export type AdminEvent = PublicEvent & {
  capacity: number | null;
  /** Gespeicherter Schalter (unabhängig davon, ob der Termin schon vorbei ist). */
  registrationOpenSetting: boolean;
  isActive: boolean;
  archivedAt: string | null;
  takenSeats: number;
  registeredCount: number;
  cancelledCount: number;
  createdAt: string;
  updatedAt: string;
};

export type AdminRegistration = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  plusOne: boolean;
  status: "registered" | "cancelled";
  cancelledAt: string | null;
  cancelledBy: string | null;
  confirmationSentAt: string | null;
  reminder7SentAt: string | null;
  reminder1SentAt: string | null;
  /** Zuletzt erfolgreich eingereihte „Nachricht an Teilnehmende“. */
  lastMessageSentAt: string | null;
  createdAt: string;
};

/** Nachricht des Teams an alle angemeldeten Gäste einer Veranstaltung. */
export const eventMessageSchema = z.object({
  eventId: z.string().uuid(),
  subject: z.string().trim().min(3, "Bitte einen Betreff angeben").max(200),
  bodyMd: z.string().trim().min(10, "Bitte einen Text angeben").max(5000),
});
export type EventMessageInput = z.infer<typeof eventMessageSchema>;

export type EventMessageResult = { recipients: number; sent: number; skipped: number; failed: number };

/**
 * Empfänger einer Nachricht: nur nicht abgesagte Anmeldungen, je Anmeldung
 * höchstens einmal (bereits erfolgreich versandte werden übersprungen).
 */
export function planMessageRecipients<T extends { id: string; status: string }>(
  regs: T[],
  alreadySent: ReadonlySet<string>,
): { send: T[]; skipped: number } {
  const active = regs.filter((r) => r.status === "registered");
  const send = active.filter((r) => !alreadySent.has(r.id));
  return { send, skipped: active.length - send.length };
}

export const eventSaveSchema = z.object({
  id: z.string().uuid().optional(),
  title: z.string().trim().min(1, "Bitte einen Titel angeben").max(200),
  startsAt: z.string().datetime({ offset: true }),
  location: z.string().trim().max(300),
  contentMd: z.string().max(50_000),
  capacity: z.number().int().min(1).max(10_000).nullable(),
  registrationOpen: z.boolean(),
  closedMessage: z.string().trim().max(1000),
});
export type EventSaveInput = z.infer<typeof eventSaveSchema>;

export const eventRegistrationSchema = z.object({
  eventId: z.string().uuid(),
  firstName: z.string().trim().max(100).optional().or(z.literal("")),
  lastName: z.string().trim().min(1, "Bitte Nachnamen angeben").max(100),
  email: z.string().trim().email("Bitte eine gültige E-Mail-Adresse angeben").max(255),
  phone: z
    .string()
    .trim()
    .min(5, "Bitte eine Telefonnummer angeben")
    .max(40)
    .regex(/^[+0-9 ()/.-]+$/, "Bitte nur Ziffern, Leerzeichen, +, / und - verwenden"),
  plusOne: z.boolean(),
  privacy: z.literal(true, { message: "Bitte den Datenschutzhinweis bestätigen" }),
  turnstileToken: z.string().min(1, "Bot-Schutz nicht bestätigt"),
});
export type EventRegistrationInput = z.infer<typeof eventRegistrationSchema>;

export type RegistrationOutcome =
  | { status: "ok" }
  | { status: "full"; free: number }
  | { status: "duplicate" }
  | { status: "closed" };

export type CancelLookup =
  | { status: "invalid" }
  | { status: "already"; title: string }
  | { status: "open"; title: string; startsAt: string; plusOne: boolean };

export const cancelTokenSchema = z.object({ token: z.string().regex(/^[a-f0-9]{48}$/) });

/** Belegte Plätze: jede Anmeldung zählt einen Platz, mit Begleitung zwei. */
export function seatsOf(rows: Array<{ plus_one: boolean; status: string }>): number {
  return rows
    .filter((r) => r.status === "registered")
    .reduce((sum, r) => sum + (r.plus_one ? 2 : 1), 0);
}

export function freeSeats(capacity: number | null, taken: number): number | null {
  if (capacity === null) return null;
  return Math.max(capacity - taken, 0);
}

/** Datum und Uhrzeit in deutscher Schreibweise (Europe/Berlin). */
export function formatEventDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const date = new Intl.DateTimeFormat("de-DE", {
    dateStyle: "long",
    timeZone: "Europe/Berlin",
  }).format(d);
  const time = new Intl.DateTimeFormat("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Berlin",
  }).format(d);
  return `${date}, ${time} Uhr`;
}

/**
 * Welche Erinnerung ist am Lauf-Tag fällig? Vergleich in ganzen Kalendertagen
 * (Europe/Berlin): 7 Tage vorher bzw. 1 Tag vorher. Fällt ein Lauf aus, wird
 * die Wochen-Erinnerung bis zu zwei Tage nachgeholt (Tag 6 und 5). Wer sich
 * erst nach dem jeweiligen Stichtag anmeldet, erhält diese Erinnerung nicht –
 * die Bestätigung nennt den Termin bereits.
 */
export function dueReminder(
  startsAt: string,
  now: Date,
  reg: { createdAt: string; reminder7: boolean; reminder1: boolean },
): 7 | 1 | null {
  const days = calendarDaysUntil(startsAt, now);
  const daysAtSignup = calendarDaysUntil(startsAt, new Date(reg.createdAt));
  if (days === null || daysAtSignup === null || days < 1) return null;
  if (days === 1 && !reg.reminder1 && daysAtSignup > 1) return 1;
  if (days >= 5 && days <= 7 && !reg.reminder7 && daysAtSignup > 7) return 7;
  return null;
}

function berlinDay(d: Date): number {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
  return Math.floor(new Date(`${parts}T00:00:00.000Z`).getTime() / 86_400_000);
}

export function calendarDaysUntil(startsAt: string, now: Date): number | null {
  const start = new Date(startsAt);
  if (Number.isNaN(start.getTime())) return null;
  return berlinDay(start) - berlinDay(now);
}

/**
 * Erstes Bild aus dem Veranstaltungstext (Markdown oder HTML) als absolute
 * Adresse — wird als Vorschaubild beim Teilen (WhatsApp, LinkedIn …) genutzt.
 * Relative Pfade werden ausgelassen, weil Vorschauen nur absolute Adressen laden.
 */
export function firstEventImageUrl(contentMd: string | null | undefined): string | null {
  if (!contentMd) return null;
  const md = contentMd.match(/!\[[^\]]*\]\((https:\/\/[^\s)]+)\)/);
  if (md?.[1]) return md[1];
  const html = contentMd.match(/<img[^>]+src=["'](https:\/\/[^"']+)["']/i);
  return html?.[1] ?? null;
}
